import moment from 'moment-timezone'
import { formatCurrency } from '@smarttransit/common'
import { isAuthorized, addAlert } from '../../utilities/helpers'
import { findTransportationProfiles } from '../../services/transportation-profiles-service'
import {
  createAdHocRevenueTransaction,
  updateAdHocRevenueTransaction
} from '../../services/passenger-transaction-logs-service'

function normalizeRevenueDate (dateValue) {
  if (!dateValue) {
    return null
  }

  const dateAsMoment = moment(dateValue)

  if (!dateAsMoment.isValid()) {
    return null
  }

  return dateAsMoment.format('Y-MM-DD')
}

export default {
  name: 'ad-hoc-revenue-dialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    signedInUser: {
      type: Object,
      default: null
    },
    initialRevenue: {
      type: Object,
      default: null
    },
    lockedVehicle: {
      type: Object,
      default: null
    },
    transportationOwnerId: {
      type: [String, Number],
      default: null
    }
  },
  data () {
    return {
      vehiclesApiInProgress: false,
      vehiclesLoaded: [],
      vehicles: [],
      adHocVehicleKeywords: '',
      addingAdhocRevenue: false,
      adhocRevenue: null
    }
  },
  computed: {
    dialogModel: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    isVehicleLocked () {
      return !!(this.lockedVehicle && this.lockedVehicle.id)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (val) {
        if (val) {
          this.initAdHocRevenue()
          this.loadVehiclesIfNeeded()
        }
      }
    },
    initialRevenue: {
      deep: true,
      handler () {
        if (this.value) {
          this.initAdHocRevenue()
        }
      }
    },
    lockedVehicle: {
      deep: true,
      handler () {
        if (this.value) {
          this.initAdHocRevenue()
          this.loadVehiclesIfNeeded()
        }
      }
    },
    transportationOwnerId () {
      this.loadVehiclesIfNeeded()
    },
    adHocVehicleKeywords (val) {
      clearTimeout(this.adHocVehicleKeywordsTimeout)

      this.adHocVehicleKeywordsTimeout = setTimeout(() => {
        if (val) {
          this.vehicles = (this.vehiclesLoaded || []).filter((o) => (new RegExp(`.*${val}.*`, 'ui')).test(o.licensePlateNumber))

          if (!this.vehicles.length) {
            this.vehicles = this.vehiclesLoaded.slice()
          }
        } else {
          this.vehicles = this.vehiclesLoaded.slice()
        }
      }, 500)
    }
  },
  methods: {
    initAdHocRevenue () {
      const selectedRevenue = this.initialRevenue || {}

      this.adhocRevenue = {
        ...selectedRevenue,
        amount: selectedRevenue.amount || null,
        dateCreated: normalizeRevenueDate(selectedRevenue.dateCreated),
        notes: selectedRevenue.notes || (selectedRevenue.logMetadata && selectedRevenue.logMetadata.notes ? selectedRevenue.logMetadata.notes : ''),
        confirmationId: selectedRevenue.confirmationId || (selectedRevenue.logMetadata && selectedRevenue.logMetadata.confirmationId ? selectedRevenue.logMetadata.confirmationId : ''),
        transportationProfileId: selectedRevenue.transportationProfileId || null
      }

      if (this.isVehicleLocked) {
        this.adhocRevenue.transportationProfileId = this.lockedVehicle.id
      }
    },
    async loadVehiclesIfNeeded () {
      if (!this.value || this.isVehicleLocked) {
        return
      }

      try {
        const transportationOwnerId = this.transportationOwnerId || null
        this.vehiclesApiInProgress = true

        this.vehiclesLoaded = await findTransportationProfiles({
          join: transportationOwnerId ? {
            relation: 'transportationOwnerProfile',
            scope: { where: { transportationOwnerId } }
          } : undefined
        })

        this.vehicles = this.vehiclesLoaded.slice()
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.vehiclesApiInProgress = false
      }
    },
    closeDialog () {
      this.dialogModel = false
    },
    async saveAdhocRevenue () {
      if (!this.adhocRevenue.amount || this.adhocRevenue.amount <= 0) {
        if (isAuthorized(this.signedInUser, 'superadmin') && this.adhocRevenue.amount === 0) {
          return alert('Please enter a valid amount other than 0')
        } else if (!isAuthorized(this.signedInUser, 'superadmin') && this.adhocRevenue.amount < 0) {
          return alert('Please enter a valid amount greater than 0')
        }
      }

      if (!this.adhocRevenue.dateCreated) {
        return alert('Please select a date')
      }

      if (!this.adhocRevenue.transportationProfileId) {
        return alert('Please select a vehicle')
      }

      const confirmAmount = prompt(`Please enter the amount number ${formatCurrency(this.adhocRevenue.amount, null, '0.00')} to confirm the manual entry`)

      if (confirmAmount === formatCurrency(this.adhocRevenue.amount, null, '0.00')) {
        this.addingAdhocRevenue = true

        try {
          if (this.adhocRevenue.id) {
            await updateAdHocRevenueTransaction({
              id: this.adhocRevenue.id,
              transportationProfileId: this.adhocRevenue.transportationProfileId,
              amount: this.adhocRevenue.amount,
              dateCreated: moment(this.adhocRevenue.dateCreated, 'Y-MM-DD').utc().toDate().toISOString(),
              notes: this.adhocRevenue.notes,
              confirmationId: this.adhocRevenue.confirmationId
            })
          } else {
            await createAdHocRevenueTransaction({
              transportationProfileId: this.adhocRevenue.transportationProfileId,
              amount: this.adhocRevenue.amount,
              dateCreated: moment(this.adhocRevenue.dateCreated, 'Y-MM-DD').utc().toDate().toISOString(),
              notes: this.adhocRevenue.notes,
              confirmationId: this.adhocRevenue.confirmationId
            })
          }

          this.closeDialog()
          this.$emit('saved')
        } catch (err) {
          addAlert({ message: err, type: 'error', isModal: true })
        } finally {
          this.addingAdhocRevenue = false
        }
      } else {
        alert('Amount entered does not match the amount to confirm')
      }
    }
  },
  beforeDestroy () {
    clearTimeout(this.adHocVehicleKeywordsTimeout)
  }
}
